/**
 * Avalanche flow parameters.
 *
 * Basal friction is Voellmy with a minimum shear stress (as com1DFA's VoellmyMinShear):
 *   τ_b = μ·σ_n + ρ·g·u²/ξ + τ₀
 * μ sets where the flow decelerates and stops (roughly on slopes flatter than atan μ), ξ limits
 * the speed on steep terrain, τ₀ makes thin tails and the front come to rest instead of creeping.
 *
 * Like RAMMS' parameter sets, μ falls with avalanche volume (large avalanches run further) and
 * wet snow gets a higher μ, lower ξ and higher τ₀ (slow, dense, channelised). Values are within
 * the published ranges (RAMMS user manual; Salm et al. 1990; AvaFrame docs) and calibrated
 * against the Norwegian α–β model in the tests.
 */
export type SnowFlowType = 'dry' | 'wet';

export interface FlowParams {
  flowType: SnowFlowType;
  /** Coulomb friction coefficient. */
  mu: number;
  /** Turbulent friction (m/s²). */
  xi: number;
  /** Minimum basal shear stress (Pa). */
  tau0: number;
  /** Density of the flowing snow (kg/m³), for flow thickness. */
  flowDensity: number;
  /** Density of the deposit (kg/m³). */
  debrisDensity: number;
  /** Erosion energy e_b (J/kg): entrainment rate = τ_b·|u| / e_b (kg m⁻² s⁻¹). 0 disables. */
  erosionEnergy: number;
  /** Mass per particle (kg). */
  particleMass: number;
  /** Upper bound on particle count, including particles split off by entrainment. */
  maxParticles: number;
  /** Earth-pressure coefficient for lateral spreading. */
  pressureCoef: number;
  /** A particle stops when slower than this (m/s)… */
  stopSpeed: number;
  /** …for this long (s). */
  stopTime: number;
  /** Hard cap on simulated time (s). */
  maxTime: number;
  /** Seed for particle placement jitter (runs are reproducible). */
  seed: number;
}

export const FLOW_DEFAULTS = {
  /** 75 kg ≈ 0.25 m³ of 300 kg/m³ slab (PLAN.md §8). */
  particleMass: 75,
  /** CPU budget; WebGPU later. */
  maxParticles: 200_000,
  /** Release particles are sized so entrainment can grow the count this much within budget. */
  entrainmentHeadroom: 3,
  /**
   * τ₀ is small for dry snow: larger values stop the thin, spreading edges of the flow on steep
   * track slopes (with 50 Pa, 75 % of a test avalanche deposited on a 35° slope; with 10 Pa, 93 %
   * reached the runout zone, as observed for dense flows).
   */
  /*
   * ξ = 1000 (the channelled-terrain end of the RAMMS range): with 2000, flows that converge
   * into gullies on real terrain ran 3–7° further than the α–β model allows; with 1000, synthetic
   * gully paths land within ±1.2° and open slopes ~2.5° short (tests/avalanche-flow.test.ts).
   */
  dry: { xi: 1000, tau0: 10, flowDensity: 200, debrisDensity: 450, erosionEnergy: 8000 },
  wet: { xi: 600, tau0: 100, flowDensity: 400, debrisDensity: 550, erosionEnergy: 5000 },
  /** μ added for wet snow. */
  wetMuIncrease: 0.08,
  /**
   * Loose-snow releases (wet loose): cohesionless grains, so a dry-like τ₀ — the wet-slab 100 Pa
   * pins a 15 cm point release on a 32° slope — and a low erosion energy, so the thin flow
   * ploughs up the wet surface snow and grows on the way down, as wet loose avalanches do (a
   * 15 m³ release on a 35–45° face grows to ~250 m³, ~5 m/s, stopping at the foot of the slope).
   */
  loose: { tau0: 20, erosionEnergy: 1000 },
  pressureCoef: 1,
  stopSpeed: 0.3,
  stopTime: 0.5,
  maxTime: 600,
} as const;

/**
 * μ for a dry avalanche of `volume` m³ (release volume): 0.30 for small (~1,000 m³) down to
 * 0.16 for very large (~1,000,000 m³), log-linear in between.
 */
export function muForVolume(volume: number): number {
  const v = Math.max(1, volume);
  const mu = 0.3 - 0.047 * Math.log10(v / 1000);
  return Math.min(0.32, Math.max(0.155, mu));
}

/** Mass per particle for a release of `releaseMass` kg: the default, coarsened to fit the budget. */
export function particleMassFor(releaseMass: number, overrideKg?: number): number {
  const base = overrideKg && overrideKg > 0 ? overrideKg : FLOW_DEFAULTS.particleMass;
  const needed = (releaseMass * FLOW_DEFAULTS.entrainmentHeadroom) / FLOW_DEFAULTS.maxParticles;
  return Math.max(base, needed);
}

export function defaultFlowParams(
  flowType: SnowFlowType,
  releaseVolume: number,
  releaseMass: number,
  overrides: Partial<FlowParams> = {},
): FlowParams {
  const set = FLOW_DEFAULTS[flowType];
  const mu = muForVolume(releaseVolume) + (flowType === 'wet' ? FLOW_DEFAULTS.wetMuIncrease : 0);
  return {
    flowType,
    mu,
    xi: set.xi,
    tau0: set.tau0,
    flowDensity: set.flowDensity,
    debrisDensity: set.debrisDensity,
    erosionEnergy: set.erosionEnergy,
    particleMass: particleMassFor(releaseMass, overrides.particleMass),
    maxParticles: FLOW_DEFAULTS.maxParticles,
    pressureCoef: FLOW_DEFAULTS.pressureCoef,
    stopSpeed: FLOW_DEFAULTS.stopSpeed,
    stopTime: FLOW_DEFAULTS.stopTime,
    maxTime: FLOW_DEFAULTS.maxTime,
    seed: 1,
    ...overrides,
    // Keep the budget-derived particle mass unless explicitly overridden above it.
    ...(overrides.particleMass === undefined
      ? {}
      : { particleMass: particleMassFor(releaseMass, overrides.particleMass) }),
  };
}

/** EAWS avalanche size (1–5) from deposit volume (m³), using the typical volumes per class. */
export function sizeClass(volume: number): 1 | 2 | 3 | 4 | 5 {
  if (volume < 300) return 1;
  if (volume < 3_000) return 2;
  if (volume < 30_000) return 3;
  if (volume < 300_000) return 4;
  return 5;
}
